/**
 * MarketplaceRow — compact list row for an Amazon Ads marketplace profile.
 *
 * Emoji flag on the left, name + marketplace ID on the right.
 */
import type { AmazonProfile } from "@adpilot/shared";
import { IconCheck } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

/** Convert a 2-letter ISO 3166-1 alpha-2 country code to a flag emoji. */
function countryCodeToFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

/** Map Amazon Ads country codes for emoji flag rendering. */
function toFlagEmoji(amazonCountry: string): string {
  // Amazon uses "UK" for United Kingdom
  if (amazonCountry === "UK") return "🇬🇧";
  return countryCodeToFlag(amazonCountry);
}

interface MarketplaceCardProps {
  profile: AmazonProfile;
  /** Stagger index for entrance animation delay (0-based). */
  index?: number;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** Shown but not selectable — e.g. a profile another Workspace already binds. */
  disabled?: boolean;
  /** Trailing status, such as a badge explaining why the row is disabled. */
  status?: React.ReactNode;
}

export function MarketplaceCard({
  profile,
  index = 0,
  selected,
  onSelectedChange,
  disabled = false,
  status,
}: MarketplaceCardProps) {
  const flag = toFlagEmoji(profile.countryCode);
  const selectable = selected !== undefined && onSelectedChange !== undefined;
  const content = (
    <>
      {selectable ? (
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
            selected ? "border-ink bg-ink text-canvas" : "border-input bg-surface text-transparent",
          )}
          aria-hidden
        >
          <IconCheck className="size-3" stroke={2.5} />
        </span>
      ) : null}
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-subtle text-base leading-none">
        {flag}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-[13px] font-medium leading-[18px] text-foreground">
          {profile.name}
        </span>
        <span className="block truncate font-mono text-[11px] leading-[15px] text-muted-foreground">
          {profile.marketplaceName} · Profile {profile.profileId}
        </span>
      </span>
      {status ? <span className="shrink-0">{status}</span> : null}
    </>
  );

  return selectable ? (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Select ${profile.name}`}
      disabled={disabled}
      onClick={() => onSelectedChange(!selected)}
      className={cn(
        "flex h-[62px] w-full items-center gap-3 px-3 text-left outline-none transition-colors animate-card-enter hover:bg-subtle focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent",
        !selected && "bg-canvas/70",
      )}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {content}
    </button>
  ) : (
    <div
      className="flex items-center gap-3 py-2.5 animate-card-enter"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {content}
    </div>
  );
}
